import { useEffect, useState } from "react";
import {
  login,
  loginWithGoogle,
  resetPassword,
  registerAndSendVerification,
  checkVerificationAndDelete,
  logout,
} from "@auth/auth";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import JOTR from "@assets/favIcon.png";
import { useAuth } from "@context/authcontext";
import { InputField, Button } from "@cakrawalajakarta/shared-components";

const loginSchema = z.object({
  email: z.string().email("Email tidak valid"),
  password: z.string().min(6, "Password minimal 6 karakter"),
});

const resetPasswordSchema = z.object({
  email: z.string().email("Email tidak valid"),
});

const registerSchema = z.object({
  name: z.string().min(2, "Nama minimal 2 karakter"),
  email: z.string().email("Email tidak valid"),
  password: z.string().min(6, "Password minimal 6 karakter"),
});

export const Login = () => {
  const navigate = useNavigate();
  const { user, logout: contextLogout } = useAuth();
  const [formMode, setFormMode] = useState("login");
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showInputs, setShowInputs] = useState(false);

  const {
    register: registerLogin,
    handleSubmit: handleSubmitLogin,
    setValue: setValueLogin,
    formState: { errors: errorsLogin, isSubmitting: isSubmittingLogin },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: errorsReset, isSubmitting: isSubmittingReset },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
  });

  const {
    register: registerReg,
    handleSubmit: handleSubmitReg,
    formState: { errors: errorsReg, isSubmitting: isSubmittingReg },
  } = useForm({
    resolver: zodResolver(registerSchema),
  });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (formMode === "login") {
      const verifiedEmail = localStorage.getItem("verifiedEmail");
      if (verifiedEmail) {
        setValueLogin("email", verifiedEmail);
        localStorage.removeItem("verifiedEmail");
      }
    }
  }, [formMode, setValueLogin]);

  useEffect(() => {
    if (user) {
      navigate("/Profile");
    }
  }, [user, navigate]);

  useEffect(() => {
    setErrorMessage(null);
    setSuccessMessage(null);
  }, [formMode]);

  const handleModeChange = (mode) => {
    setFormMode(mode);
    setErrorMessage(null);
    setSuccessMessage(null);
    if (mode === "login") {
      setShowInputs(false);
    }
  };

  const handleLogin = async (data) => {
    try {
      setErrorMessage(null);
      await contextLogout();
      const user = await login(data.email, data.password);

      if (!user.emailVerified) {
        setErrorMessage("Silakan verifikasi email Anda sebelum login.");
        await contextLogout();
        return;
      }

      navigate("/Profile");
    } catch (error) {
      const errorCode =
        error?.code ||
        error?.message?.match(/\(auth\/(.*?)\)/)?.[1] ||
        "unknown_error";

      switch (errorCode) {
        case "user-not-found":
          setErrorMessage(
            "Email tidak terdaftar. Silakan daftar terlebih dahulu."
          );
          break;
        case "wrong-password":
          setErrorMessage("Password salah. Silakan coba lagi.");
          break;
        case "invalid-email":
          setErrorMessage("Format email tidak valid.");
          break;
        case "too-many-requests":
          setErrorMessage("Terlalu banyak percobaan login. Coba lagi nanti.");
          break;
        case "invalid-credential":
          setErrorMessage("Email atau password anda salah.");
          break;
        default:
          setErrorMessage("Email belum diverifikasi. Silakan cek email Anda.");
      }
    }
  };

  const handleGoogleLogin = async () => {
    setLoadingGoogle(true);
    setErrorMessage(null);
    try {
      await loginWithGoogle();
      navigate("/Profile");
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleResetPassword = async (data) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await resetPassword(data.email);
      setSuccessMessage("Link reset password telah dikirim ke email Anda.");
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const handleRegister = async (data) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const { message } = await registerAndSendVerification(
        data.email,
        data.name,
        data.password
      );
      setSuccessMessage(message);

      setTimeout(async () => {
        await checkVerificationAndDelete();
      }, 120000);

      setTimeout(() => {
        setFormMode("login");
        setShowInputs(false);
      }, 5000);
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-20">
      <motion.div
        className="relative z-0 max-w-4xl w-full mx-auto bg-white bg-opacity-40 border border-slate-200 backdrop-blur-md grid md:grid-cols-2 rounded-xl overflow-hidden"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <div
          className="hidden md:flex flex-col justify-center text-white bg-cover bg-center p-8 h-full relative rounded-tl-xl rounded-bl-xl"
          style={{
            backgroundImage: "url('/bg/bghero11.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-lime-600/100 to-lime-600/20 z-0 rounded-tl-xl rounded-bl-xl"></div>

          <motion.h1
            className="text-4xl font-bold mb-4 z-0 relative text-white"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Selamat Datang
          </motion.h1>
          <motion.p
            className="text-lg z-20 relative text-white"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
          >
           Masuk untuk mengakses akun Anda dan melakukan registrasi.
          </motion.p>
        </div>

        <div className="flex flex-col px-4 py-8 z-10">
          <AnimatePresence mode="wait">
            {formMode === "login" && (
              <motion.div
                key="login"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex flex-col gap-3"
              >
                <img
                  src={JOTR}
                  alt="Logo"
                  className="w-24 h-auto mx-auto mt-4 mb-2 block"
                />
                <p className="text-xl font-semibold mb-4 text-center text-lime-600">
                  JOTR GEPANG SERIES 2025
                </p>

                {!showInputs && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={handleGoogleLogin}
                      className="w-full"
                      disabled={loadingGoogle}
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.51h5.84c-.25 1.37-1.02 2.53-2.16 3.31v2.75h3.49c2.04-1.88 3.23-4.65 3.23-7.77z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-1.01 7.28-2.73l-3.49-2.75c-1.01.68-2.3 1.08-3.79 1.08-2.91 0-5.37-1.97-6.25-4.61H2.36v2.9C4.17 20.99 7.83 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.75 14.24c-.22-.66-.34-1.37-.34-2.11s.12-1.45.34-2.11V7.12H2.36C1.49 8.71 1 10.34 1 12s.49 3.29 1.36 4.88l3.39-2.64z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.02c1.61 0 3.04.55 4.18 1.62l3.13-3.13C17.46 1.99 14.97 1 12 1 7.83 1 4.17 3.01 2.36 6.88l3.39 2.64c.88-2.64 3.34-4.5 6.25-4.5z"
                          fill="#EA4335"
                        />
                      </svg>
                      {loadingGoogle ? "Memproses..." : "Login dengan Google"}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setShowInputs(true)}
                      className="w-full"
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                          stroke="#4B5563"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M22 6L12 13L2 6"
                          stroke="#4B5563"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Login dengan Email
                    </Button>
                    <p className="text-slate-600 text-sm text-center mt-12 px-4">
                      Dengan bergabung, Anda menyetujui{" "}
                      <Link
                        to="/SyaratKetentuan"
                        className="text-lime-800 hover:underline"
                      >
                        Syarat dan Ketentuan
                      </Link>{" "}
                      kami. Silakan baca{" "}
                      <Link
                        to="/KebijakanPrivasi"
                        className="text-lime-800 hover:underline"
                      >
                        Kebijakan Privasi
                      </Link>{" "}
                      untuk memahami pengelolaan data pribadi Anda.
                    </p>
                  </>
                )}

                {showInputs && (
                  <motion.form
                    onSubmit={handleSubmitLogin(handleLogin)}
                    className="flex flex-col gap-3"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <InputField
                      label="Email"
                      name="email"
                      type="email"
                      {...registerLogin("email")}
                      placeholder="Email"
                      error={errorsLogin.email?.message}
                      labelColor="text-slate-800"
                    />
                    <InputField
                      label="Password"
                      name="password"
                      type="password"
                      {...registerLogin("password")}
                      placeholder="Password"
                      error={errorsLogin.password?.message}
                      labelColor="text-slate-800"
                    />
                    {errorMessage && (
                      <p className="text-sm text-red-600 text-center -mt-2">
                        {errorMessage}
                      </p>
                    )}

                    <Button
                      type="submit"
                      variant="primary"
                      className="mt-4 w-full"
                      disabled={isSubmittingLogin}
                    >
                      {isSubmittingLogin ? "Memproses..." : "Login"}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => handleModeChange("forgot-password")}
                      className="mt-4 w-full text-lime-800"
                    >
                      Lupa password?
                    </Button>

                    <div className="text-center text-sm text-slate-800">
                      <p>
                        Belum punya akun?{" "}
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => handleModeChange("register")}
                          className="text-lime-800 px-1 h-auto"
                        >
                          Daftar di sini
                        </Button>
                      </p>
                    </div>
                  </motion.form>
                )}
              </motion.div>
            )}

            {formMode === "forgot-password" && (
              <motion.div
                key="forgot-password"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex flex-col gap-3"
              >
                <h2 className="text-2xl font-semibold text-center mb-6 text-slate-800">
                  Lupa Password
                </h2>
                <form
                  onSubmit={handleSubmitReset(handleResetPassword)}
                  className="flex flex-col gap-3"
                >
                  <InputField
                    label="Email"
                    name="email"
                    type="email"
                    {...registerReset("email")}
                    placeholder="Masukkan email"
                    error={errorsReset.email?.message}
                    labelColor="text-slate-800"
                  />
                  {errorMessage && (
                    <p className="text-sm text-red-600 text-center -mt-2">
                      {errorMessage}
                    </p>
                  )}
                  {successMessage && (
                    <p className="text-sm text-slate-800 text-center -mt-2">
                      {successMessage}
                    </p>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    className="mt-4 w-full h-10"
                    disabled={isSubmittingReset}
                  >
                    {isSubmittingReset ? "Mengirim..." : "Kirim Link Reset"}
                  </Button>
                </form>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => handleModeChange("login")}
                  className="mt-4 w-full text-lime-800"
                >
                  Kembali ke Login
                </Button>
              </motion.div>
            )}

            {formMode === "register" && (
              <motion.div
                key="register"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex flex-col gap-3"
              >
                <p className="text-2xl font-semibold mb-6 text-center text-slate-800">
                  Daftar
                </p>
                <form
                  onSubmit={handleSubmitReg(handleRegister)}
                  className="flex flex-col gap-3"
                >
                  <InputField
                    label="Nama"
                    name="name"
                    type="text"
                    {...registerReg("name")}
                    placeholder="Nama Lengkap"
                    error={errorsReg.name?.message}
                    labelColor="text-slate-800"
                  />
                  <InputField
                    label="Email"
                    name="email"
                    type="email"
                    {...registerReg("email")}
                    placeholder="Email"
                    error={errorsReg.email?.message}
                    labelColor="text-slate-800"
                  />
                  <InputField
                    label="Password"
                    name="password"
                    type="password"
                    {...registerReg("password")}
                    placeholder="Password"
                    error={errorsReg.password?.message}
                    labelColor="text-slate-800"
                  />
                  {errorMessage && (
                    <p className="text-sm text-red-600 text-center -mt-2">
                      {errorMessage}
                    </p>
                  )}
                  {successMessage && (
                    <p className="text-sm text-slate-800 text-center -mt-2">
                      {successMessage}
                    </p>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    className="mt-4 w-full h-10"
                    disabled={isSubmittingReg}
                  >
                    {isSubmittingReg ? "Memproses..." : "Daftar"}
                  </Button>
                </form>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => handleModeChange("login")}
                  className="mt-4 w-full text-lime-800"
                >
                  Kembali ke Login
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};
